// The section «Аналитика» (R-AN1): a minimal start, marked «Раздел в работе» — the numbers of the chosen area (the whole account, a
// record above the scope records or one of them) for a period, each counted on the server through the analytics API with the
// person's rights. No charts and no builder: what is shown is what is counted.

import { useMemo, useState } from 'react';
import { recordTitle, type Id } from '@confdeck/core-shared';
import { useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { Page, PageBlock } from '../ui/Page.tsx';
import { Segmented } from '../ui/controls.tsx';
import { Select } from '../ui/menus.tsx';
import { useCatalog, type MetricLevel, type Period } from './api.ts';
import { MetricTiles } from './MetricTiles.tsx';
import s from './analytics.module.css';

type Span = 'all' | '30' | '90' | '365';
const SPANS: { value: Span; label: string }[] = [{ value: '30', label: '30 дней' }, { value: '90', label: '90 дней' }, { value: '365', label: 'Год' }, { value: 'all', label: 'Всё время' }];

/** The last N days up to today, today included; local days, as the person reads the calendar. */
export function periodOf(span: Span, now = new Date()): Period {
  if (span === 'all') return 'all';
  const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const from = new Date(now);
  from.setDate(from.getDate() - Number(span) + 1);
  return { from: day(from), to: day(now) };
}

export function AnalyticsScreen() {
  const { store } = useSync();
  const meta = useMeta();
  const { catalog, error } = useCatalog();
  const [area, setArea] = useState('');
  const [span, setSpan] = useState<Span>('30');
  useTypesTick([catalog?.types.parent, catalog?.types.scope].filter((x): x is Id => !!x));
  const options = useMemo(() => {
    if (!catalog) return [];
    const of = (typeId: Id | null, word: string | null) => (typeId && word ? [...store.recordIds(typeId)].map((rid) => ({ value: rid, label: `${word}: ${recordTitle(store.record(rid), meta)}` })).sort((a, b) => a.label.localeCompare(b.label, 'ru')) : []);
    return [{ value: '', label: catalog.levels.account }, ...of(catalog.types.parent, catalog.levels.parent), ...of(catalog.types.scope, catalog.levels.scope)];
  }, [catalog, store, meta, store.typeVersion.get(catalog?.types.scope ?? ''), store.typeVersion.get(catalog?.types.parent ?? '')]);
  const typeOfArea = area ? store.record(area)?.typeId : null;
  const level: MetricLevel = !area ? 'account' : typeOfArea === catalog?.types.scope ? 'scope' : 'parent';
  const period = periodOf(span);
  const fits = (catalog?.metrics ?? []).filter((m) => m.levels.includes(level));
  const own = fits.filter((m) => m.app);
  const sections = fits.filter((m) => !m.app && m.key.startsWith('count:'));
  return (
    <Page title="Аналитика" icon="chart">
      <div className={s.notice} role="note">
        <p className={s.noticeTitle}>Раздел в работе</p>
        <p className={s.noticeText}>Пока здесь только числа выбранной области за период — они считаются на сервере по вашим правам. Графики, сравнения, воронки продаж и выгрузка появятся позже.</p>
      </div>
      {error && <p className={s.error} role="alert">{error}</p>}
      {catalog && (
        <>
          <div className={s.bar}>
            <Select label="Область" value={area} options={options} onChange={setArea} search={options.length > 8} className={s.area} />
            <Segmented label="Период" size="sm" value={span} onChange={setSpan} items={SPANS} />
          </div>
          <PageBlock title="Главные числа" help="Числа приложений; ниже — прошлый период той же длины" label="Главные числа">
            <MetricTiles metrics={own} scope={area || null} period={period} compare label="Главные числа" />
          </PageBlock>
          <PageBlock title="Записи разделов" help="Сколько записей создано за период — по дате создания записи" label="Записи разделов">
            <MetricTiles metrics={sections} scope={area || null} period={period} label="Записи разделов" />
          </PageBlock>
        </>
      )}
    </Page>
  );
}
